import React, { useEffect, useId, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Copy, Image as ImageIcon, Loader2, Package, RefreshCw, Wallet, X } from 'lucide-react';
import {
  displayText, formatRupiah, formatTrackingDate, hasAssetId, maskPhone,
  recipientAddress, refundStatus, safeImageDataUri, shippingStatus,
} from './trackingHelpers';
import { formatWeightKg, paymentMethod, shippingFeeLabel } from './orderDisplayHelpers';
import { createProofMemoryCache, PROOF_VERSION } from './proofMemoryCache';
import { refundDepositPresentation } from './refundDepositPresentation';
import { hasVerifiedPaymentTotal } from './paymentReconciliation';
import './OrderTrackingDetails.css';

function StatusBadge({ status }) {
  return <span className={`wx-tracking-badge wx-tracking-badge--${status.tone}`}>{status.label}</span>;
}

function TrackingNumber({ number, packageLabel }) {
  const value = displayText(number);
  const [copyState, setCopyState] = useState('idle');
  const input = useRef(null);
  const timer = useRef(null);
  const generation = useRef(0);
  const id = useId();

  useEffect(() => {
    generation.current += 1;
    setCopyState('idle');
    return () => {
      generation.current += 1;
      clearTimeout(timer.current);
    };
  }, [value]);

  async function copy() {
    const current = ++generation.current;
    clearTimeout(timer.current);
    setCopyState('copying');
    try {
      if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
        throw new Error('CLIPBOARD_UNAVAILABLE');
      }
      await navigator.clipboard.writeText(value);
      if (generation.current !== current) return;
      setCopyState('copied');
      timer.current = setTimeout(() => {
        if (generation.current === current) setCopyState('idle');
      }, 3000);
    } catch (_) {
      if (generation.current !== current) return;
      setCopyState('manual');
      input.current?.focus();
      input.current?.select();
    }
  }

  return (
    <div className="wx-tracking-resi">
      <label className="wx-tracking-label" htmlFor={id}>Nomor resi</label>
      {value ? <>
        <div className="wx-tracking-copy-row">
          <input ref={input} id={id} readOnly value={value} aria-label={`Nomor resi ${packageLabel}`} />
          <button type="button" className="wx-tracking-button" onClick={copy} disabled={copyState === 'copying'}
            aria-label={`Salin nomor resi ${packageLabel}`}>
            <Copy size={15} aria-hidden="true" />
            {copyState === 'copied' ? 'Tersalin' : copyState === 'copying' ? 'Menyalin…' : 'Salin'}
          </button>
        </div>
        <p className="wx-tracking-copy-feedback" role="status" aria-live="polite">
          {copyState === 'copied' ? 'Nomor resi berhasil disalin.' : copyState === 'manual'
            ? 'Salin otomatis tidak tersedia. Pilih nomor resi di atas, lalu salin secara manual.' : ''}
        </p>
      </> : <p className="wx-tracking-muted">Resi belum tersedia.</p>}
    </div>
  );
}

function SectionState({ name, items, loading, error, unavailable, onRetry, children }) {
  if (loading) return <div className="wx-tracking-state" role="status"><Loader2 size={18} className="wx-tracking-spin" aria-hidden="true" />Memuat {name.toLowerCase()}…</div>;
  if (error || unavailable || !Array.isArray(items)) {
    return <div className="wx-tracking-state wx-tracking-state--unavailable">
      <p role="status">{error ? `Data ${name.toLowerCase()} belum dapat dimuat.` : `Data ${name.toLowerCase()} belum tersedia.`}</p>
      {typeof onRetry === 'function' && <button type="button" className="wx-tracking-button" onClick={onRetry}><RefreshCw size={15} aria-hidden="true" />Coba lagi</button>}
    </div>;
  }
  if (items.length === 0) return <><p className="wx-tracking-state">{name === 'Pengiriman' ? 'Belum ada paket pengiriman untuk pesanan ini.' : name === 'Pembayaran' ? 'Belum ada pembayaran terverifikasi untuk pesanan ini.' : 'Belum ada refund untuk pesanan ini.'}</p>{name === 'Pembayaran' ? children : null}</>;
  return children;
}

/**
 * Display-only detail below the existing financial summary.
 * Missing arrays remain unavailable; only explicit [] means confirmed empty.
 * onLoadAsset({ kind: 'refund' | 'shipping_photo' | 'payment', id }) resolves
 * { mime_type, data_base64 }. It is called only after a click or retry.
 */
export default function OrderTrackingDetails({
  packages, refunds, payments, paymentsAvailable, reconciliation, summary, depositSummary, loading = false, error = false, trackingAvailable,
  contractMissing = false, onRetry, onLoadAsset, className = '',
}) {
  const [paymentsExpanded, setPaymentsExpanded] = useState(false);
  const [selected, setSelected] = useState(null);
  const [asset, setAsset] = useState(null);
  const [assetState, setAssetState] = useState('idle');
  const request = useRef(0);
  const assetController = useRef(null);
  const proofCache = useRef(null);
  if (!proofCache.current) proofCache.current = createProofMemoryCache();
  const returnFocus = useRef(null);
  const paymentHeading = useId();
  const paymentList = useId();
  const shippingHeading = useId();
  const refundHeading = useId();
  const unavailable = contractMissing || trackingAvailable === false;
  const canLoad = typeof onLoadAsset === 'function';

  // Changes of order, loading state, or availability invalidate in-flight assets.
  // The integration should additionally key this component by the order ID.
  useEffect(() => {
    request.current += 1;
    assetController.current?.abort();
    proofCache.current.clear();
    setSelected(null);
    setAsset(null);
    setAssetState('idle');
    return () => { request.current += 1; assetController.current?.abort(); proofCache.current.clear(); };
  }, [packages, refunds, payments, loading, error, trackingAvailable, paymentsAvailable, contractMissing, onLoadAsset]);

  useEffect(() => { setPaymentsExpanded(false); }, [payments, paymentsAvailable]);

  function closeAsset() {
    request.current += 1;
    assetController.current?.abort();
    setSelected(null);
    setAsset(null);
    setAssetState('idle');
  }

  async function loadAsset(selection, force = false) {
    const current = ++request.current;
    assetController.current?.abort();
    const controller = new AbortController();
    assetController.current = controller;
    const key = `${selection.kind}:${selection.id}`;
    if (force) proofCache.current.remove(key);
    // Only refund supports conditional responses; payment/shipping remain uncached.
    const cached = selection.kind === 'refund' ? proofCache.current.get(key) : null;
    setAsset(null);
    setAssetState('loading');
    try {
      const query = { kind: selection.kind, id: selection.id };
      if (cached) query.known_version = cached.version;
      const response = await onLoadAsset(query, { signal: controller.signal });
      if (current !== request.current || controller.signal.aborted) return;
      let src;
      if (response?.not_modified === true) {
        if (!cached || response.version !== cached.version || response.mime_type !== cached.mime_type) throw new Error('Invalid proof validation');
        src = cached.src;
      } else {
        src = safeImageDataUri(response);
        proofCache.current.remove(key);
        if (selection.kind === 'refund' && PROOF_VERSION.test(response?.version || '')) {
          proofCache.current.put(key, { version: response.version, src, mime_type: response.mime_type });
        }
      }
      setAsset({ src, request: current, key });
      setAssetState('decoding');
    } catch (_) {
      if (current !== request.current || controller.signal.aborted) return;
      // Never display old bytes after denial, malformed validation, or transport error.
      proofCache.current.clear();
      setAsset(null);
      setAssetState('error');
    }
  }

  function openAsset(selection, event) {
    if (!canLoad || !hasAssetId(selection.id)) return;
    returnFocus.current = event.currentTarget;
    setSelected(selection);
    loadAsset(selection);
  }

  const validPayments = Array.isArray(payments) ? payments : [];
  const shownPayments = paymentsExpanded ? validPayments : validPayments.slice(0, 2);
  const validPackages = Array.isArray(packages) ? packages : [];
  const validRefunds = Array.isArray(refunds) ? refunds.filter((refund) => refund &&
    !['TIDAK_BERLAKU_KOREKSI', 'DIBATALKAN', 'JADI_DEPOSIT'].includes(refund.status) && refund.decision !== 'DEPOSIT') : [];
  const reconciled = hasVerifiedPaymentTotal(reconciliation, summary);
  const adjustments = Array.isArray(reconciliation?.adjustments) ? reconciliation.adjustments : [];

  return <div className={`wx-order-tracking ${className}`}>
    <section className="wx-card wx-tracking-section wx-payment-section" aria-labelledby={paymentHeading}>
      <div className="wx-tracking-section-heading"><span className="wx-tracking-icon wx-tracking-icon--green"><Wallet size={21} aria-hidden="true" /></span><div><h2 id={paymentHeading}>Pembayaran terverifikasi</h2></div></div>
      <SectionState name="Pembayaran" items={payments} loading={loading} error={error} unavailable={paymentsAvailable !== true} onRetry={onRetry}>
        <div id={paymentList} className="wx-payment-list">{shownPayments.map((payment, index) => {
          if (!payment || typeof payment !== 'object') return <p className="wx-tracking-state" key={`invalid-${index}`}>Detail pembayaran belum tersedia.</p>;
          const isDeposit = payment.kind === 'DEPOSIT_USAGE';
          const hasProof = !isDeposit && payment.proof_available === true && hasAssetId(payment.id);
          return <article className="wx-payment-item" key={`${displayText(payment.id, 'payment')}-${index}`}>
            <div className="wx-payment-row"><p className="wx-payment-method-date">{isDeposit ? 'Deposit' : paymentMethod(payment.method)}<span aria-hidden="true"> · </span>{formatTrackingDate(payment.paid_at)}</p><p className="wx-payment-amount">{formatRupiah(payment.amount)}</p></div>
            <div className="wx-payment-row"><span className="wx-payment-verified">{isDeposit ? 'Deposit terpakai' : 'Terverifikasi'}</span>{hasProof ? <button type="button" className="wx-tracking-button wx-payment-proof" disabled={!canLoad}
              onClick={(event) => openAsset({ kind: 'payment', id: payment.id, title: 'Bukti pembayaran', description: `Bukti pembayaran ${formatTrackingDate(payment.paid_at)}` }, event)}>Lihat bukti</button> : !isDeposit && <span className="wx-payment-proof-unavailable">Bukti belum tersedia</span>}</div>
          </article>;
        })}</div>
        {adjustments.length > 0 && <div className="wx-payment-adjustments">{adjustments.map((entry) => <div className="wx-payment-row" key={entry.id}>
          <span>{entry.label}</span><span>−{formatRupiah(entry.amount)}</span>
        </div>)}</div>}
        <div className="wx-payment-total" data-testid="payment-total"><span>Total pembayaran terverifikasi</span><strong>{reconciled ? formatRupiah(reconciliation.total) : '—'}</strong></div>
        {!reconciled && <p className="wx-payment-reconcile-note" role="status">{reconciliation?.state === 'mismatch' ? 'Rincian pembayaran sedang dicocokkan dengan ringkasan.' : 'Total rincian pembayaran belum dapat diverifikasi.'}</p>}
        <div className="wx-payment-footer">
          <p className="wx-payment-count" aria-live="polite">Menampilkan {shownPayments.length} dari {validPayments.length} pembayaran</p>
          {validPayments.length > 2 && <button type="button" className="wx-tracking-button wx-payment-toggle" aria-expanded={paymentsExpanded} aria-controls={paymentList}
            onClick={() => setPaymentsExpanded((value) => !value)}>{paymentsExpanded ? 'Tampilkan lebih sedikit' : `Lihat semua (${validPayments.length})`}</button>}
        </div>
      </SectionState>
    </section>
    <section className="wx-card wx-tracking-section" aria-labelledby={shippingHeading}>
      <div className="wx-tracking-section-heading"><span className="wx-tracking-icon"><Package size={21} aria-hidden="true" /></span><div><h2 id={shippingHeading}>Pengiriman</h2><p>Detail paket dan tujuan pengiriman</p></div></div>
      <SectionState name="Pengiriman" items={packages} loading={loading} error={error} unavailable={unavailable} onRetry={onRetry}>
        <div className="wx-tracking-list">{validPackages.map((pkg, index) => {
          if (!pkg || typeof pkg !== 'object') return <p className="wx-tracking-state" key={`invalid-${index}`}>Detail paket belum tersedia.</p>;
          const packageLabel = `Paket ${displayText(pkg.number, String(index + 1))}`;
          const recipient = pkg.recipient && typeof pkg.recipient === 'object' ? pkg.recipient : {};
          const photos = Array.isArray(pkg.photos) ? pkg.photos.filter((photo) => photo && hasAssetId(photo.id)) : [];
          return <article className="wx-tracking-item" key={`${displayText(pkg.id, 'package')}-${index}`}>
            <div className="wx-tracking-item-heading"><h3>{packageLabel}</h3><StatusBadge status={shippingStatus(pkg.status)} /></div>
            <dl className="wx-tracking-facts"><div><dt>Kurir</dt><dd>{displayText(pkg.courier, 'Belum tersedia')}</dd></div><div><dt>Tanggal kirim</dt><dd>{formatTrackingDate(pkg.shipped_at)}</dd></div></dl>
            <dl className="wx-tracking-facts wx-shipping-costs">
              <div><dt>Berat</dt><dd>{formatWeightKg(pkg.weight_kg)}</dd></div>
              <div><dt>Ongkir asli</dt><dd>{formatRupiah(pkg.shipping_original)}</dd></div>
              {typeof pkg.shipping_subsidy === 'number' && pkg.shipping_subsidy > 0 && <div><dt>Subsidi ongkir</dt><dd>{formatRupiah(pkg.shipping_subsidy)}</dd></div>}
              <div><dt>Ongkir akhir</dt><dd>{shippingFeeLabel(pkg.shipping_fee_state, pkg.shipping_net, formatRupiah)}</dd></div>
            </dl>
            <div className="wx-tracking-recipient"><p className="wx-tracking-label">Penerima</p><p className="wx-tracking-recipient-name">{displayText(recipient.name, 'Nama penerima belum tersedia')}</p><p className="wx-tracking-muted">{maskPhone(recipient.phone)}</p><p className="wx-tracking-address">{recipientAddress(recipient)}</p></div>
            <TrackingNumber number={pkg.tracking_number} packageLabel={packageLabel} />
            <div className="wx-tracking-photos"><p className="wx-tracking-label">Foto pengiriman</p>
              {photos.length > 0 ? <div className="wx-tracking-photo-buttons">{photos.map((photo, photoIndex) => {
                const title = displayText(photo.caption, `Foto ${photoIndex + 1}`);
                return <button key={`${photo.id}-${photoIndex}`} type="button" className="wx-tracking-button" disabled={!canLoad}
                  onClick={(event) => openAsset({ kind: 'shipping_photo', id: photo.id, title, description: `Foto pengiriman ${packageLabel}` }, event)}>
                  <ImageIcon size={16} aria-hidden="true" />{title}
                </button>;
              })}</div> : <p className="wx-tracking-muted">{Array.isArray(pkg.photos) ? 'Belum ada foto pengiriman.' : 'Data foto pengiriman belum tersedia.'}</p>}
              {photos.length > 0 && !canLoad && <p className="wx-tracking-muted">Pratinjau foto belum tersedia.</p>}
            </div>
          </article>;
        })}</div>
      </SectionState>
    </section>

    <section className="wx-card wx-tracking-section" aria-labelledby={refundHeading}>
      <div className="wx-tracking-section-heading"><span className="wx-tracking-icon wx-tracking-icon--green"><Wallet size={21} aria-hidden="true" /></span><div><h2 id={refundHeading}>Refund dan Deposit</h2></div></div>
      {(!Array.isArray(refunds) || validRefunds.length > 0 || unavailable || loading || error) && <SectionState name="Refund" items={Array.isArray(refunds) ? validRefunds : undefined} loading={loading} error={error} unavailable={unavailable} onRetry={onRetry}>
        <div className="wx-tracking-list">{validRefunds.map((refund, index) => {
          if (!refund || typeof refund !== 'object') return <p className="wx-tracking-state" key={`invalid-${index}`}>Detail refund belum tersedia.</p>;
          const conversion = refundDepositPresentation(refund);
          const title = refund.kind === 'SEBAGIAN' ? 'Refund sebagian' : refund.kind === 'PENUH' ? 'Refund penuh' : 'Refund';
          const hasProof = refund.proof_available === true && hasAssetId(refund.id);
          return <article className="wx-tracking-item" key={`${displayText(refund.id, 'refund')}-${index}`}>
            <div className="wx-tracking-item-heading"><h3>{title}</h3><StatusBadge status={conversion?.status || refundStatus(refund.status)} /></div>
            <p className="wx-tracking-label">{conversion?.completed ? conversion.amountLabel : displayText(refund.amount_label, 'Nominal pengembalian')}</p><p className="wx-tracking-amount">{formatRupiah(conversion?.completed ? conversion.amount : refund.amount)}</p>
            {conversion ? <p className="wx-tracking-muted">{conversion.message}</p> : refund.status === 'MENUNGGU_KEPUTUSAN' ? <p className="wx-tracking-muted">Metode pengembalian belum diputuskan.</p> : refund.decision === 'DEPOSIT' ? <p className="wx-tracking-muted">Tujuan pengembalian: deposit</p> : refund.decision === 'REFUND' ? <p className="wx-tracking-muted">Metode pengembalian: refund dana</p> : null}
            <dl className="wx-tracking-facts wx-tracking-refund-date"><div><dt>Tanggal proses</dt><dd>{formatTrackingDate(conversion?.completed ? conversion.date : refund.processed_at)}</dd></div></dl>
            {hasProof ? <><button type="button" className="wx-tracking-button" disabled={!canLoad}
              onClick={(event) => openAsset({ kind: 'refund', id: refund.id, title: 'Bukti refund', description: `Bukti untuk ${title.toLowerCase()} ${index + 1}` }, event)}>
              <ImageIcon size={16} aria-hidden="true" />Lihat bukti
            </button>{!canLoad && <p className="wx-tracking-muted">Pratinjau bukti belum tersedia.</p>}</> : !conversion?.completed && <p className="wx-tracking-muted">Bukti belum tersedia.</p>}
          </article>;
        })}</div>
      </SectionState>}
      <article className="wx-tracking-item wx-deposit-card">
        <p className="wx-tracking-label">Deposit tersedia untuk PO berikutnya</p>
        <p className="wx-tracking-amount">{depositSummary?.state === 'available' && Number.isSafeInteger(depositSummary.amount) && depositSummary.amount >= 0 ? formatRupiah(depositSummary.amount) : '—'}</p>
        {depositSummary?.state !== 'available' && <p className="wx-tracking-muted">Saldo deposit belum tersedia.</p>}
      </article>
    </section>

    <Dialog.Root open={selected !== null} onOpenChange={(open) => { if (!open) closeAsset(); }}>
      <Dialog.Portal><Dialog.Overlay className="wx-tracking-dialog-overlay" /><Dialog.Content className="wx-tracking-dialog"
        onCloseAutoFocus={(event) => { event.preventDefault(); if (returnFocus.current?.isConnected) returnFocus.current.focus(); }}>
        <div className="wx-tracking-dialog-heading"><div><Dialog.Title className="wx-tracking-dialog-title">{selected?.title || 'Pratinjau gambar'}</Dialog.Title><Dialog.Description className="wx-tracking-dialog-description">{selected?.description || 'Gambar bukti pesanan'}</Dialog.Description></div><Dialog.Close asChild><button type="button" className="wx-tracking-icon-button" aria-label="Tutup pratinjau"><X size={20} aria-hidden="true" /></button></Dialog.Close></div>
        <div className="wx-tracking-image-area">
          {(assetState === 'loading' || assetState === 'decoding') && <p className="wx-tracking-image-state" role="status"><Loader2 size={22} className="wx-tracking-spin" aria-hidden="true" />Memuat gambar…</p>}
          {assetState === 'error' && <div className="wx-tracking-image-state"><p role="alert">Gambar belum dapat ditampilkan. Coba muat ulang.</p><button type="button" className="wx-tracking-button" onClick={() => selected && loadAsset(selected, true)}><RefreshCw size={16} aria-hidden="true" />Coba lagi</button></div>}
          {asset && <img key={asset.request} src={asset.src} alt={selected?.title || 'Bukti pesanan'} className={`wx-tracking-image${assetState === 'decoding' ? ' wx-tracking-image--loading' : ''}`}
            onLoad={() => { if (request.current === asset.request) setAssetState('ready'); }}
            onError={() => { if (request.current === asset.request) { proofCache.current.remove(asset.key); setAsset(null); setAssetState('error'); } }} />}
        </div>
      </Dialog.Content></Dialog.Portal>
    </Dialog.Root>
  </div>;
}
